import { useEffect, useMemo, useRef, useState } from "react";
import { Input, InputNumber, Select, DatePicker, TimePicker, Slider } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { resolvePath, buildInvitePayload } from "./walker";

dayjs.extend(customParseFormat);

const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";

const parsePickerValue = (value, format) => {
  if (value === undefined || value === null || value === "") { return undefined; }
  const parsed = dayjs(value, format);
  return parsed.isValid() ? parsed : undefined;
};

// Dynamic enrollment form. Renders the questions reachable given the answers so
// far (resolvePath), revealing further questions as branch-deciding answers come
// in. Reports { complete, email, customFields } upward via onChange.
//
// initialAnswers — prefill (used in the subject profile edit, keyed by graph
//   nodeId). hiddenAliases — node aliases to keep in the path (for completeness)
//   but NOT render (e.g. 'email' in the profile, which is shown separately).
const EnrollmentForm = ({
  graph,
  fields,
  onChange,
  initialAnswers,
  hiddenAliases = [],
  borderless = false,
  fieldClassName = "enrollment-invite-field",
}) => {
  const variant = borderless ? "borderless" : "outlined";
  const [answers, setAnswers] = useState(() => initialAnswers || {});

  // Re-apply prefilled answers when they actually change (e.g. the subject loads
  // after first paint). Compared by content so plain re-renders don't wipe edits.
  const initialKeyRef = useRef(JSON.stringify(initialAnswers || {}));
  useEffect(() => {
    const key = JSON.stringify(initialAnswers || {});
    if (key !== initialKeyRef.current) {
      initialKeyRef.current = key;
      setAnswers(initialAnswers || {});
    }
  }, [initialAnswers]);

  const { nodeIds, complete } = useMemo(
    () => resolvePath(graph || {}, answers),
    [graph, answers]
  );

  const allAnswered = useMemo(
    () => nodeIds.every((id) => {
      const a = answers[id];
      if (a == null) {return false;}
      if (typeof a === "string") {return a.trim() !== "";}
      if (Array.isArray(a.answers)) {return a.answers.length > 0;}
      return a.value != null;
    }),
    [nodeIds, answers]
  );

  useEffect(() => {
    const { email, customFields } = buildInvitePayload(graph || {}, fields || [], answers, nodeIds);
    onChange({ complete: complete && allAnswered, email, customFields });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, fields, answers, complete, allAnswered]);

  const setAnswer = (nodeId, value) =>
    setAnswers((prev) => ({ ...prev, [nodeId]: value }));

  const renderField = (nodeId) => {
    const node = graph[nodeId];
    if (!node) {return null;}
    const data = node.data || {};
    // Hidden-but-required nodes (e.g. email in the profile) stay on the path for
    // completeness but aren't rendered.
    if (hiddenAliases.includes(data.alias)) {return null;}
    const label = data.message || data.label || "Untitled";
    const answer = answers[nodeId];

    let control = null;
    switch (data.nodeType) {
      case "message:single":
        control = (
          <Select
            style={{ width: "100%" }}
            variant={variant}
            placeholder={data.placeholder || "Select..."}
            value={answer ? answer.value : undefined}
            options={(data.response || []).map((o) => ({ label: o.label, value: o.value }))}
            onChange={(value, opt) => setAnswer(nodeId, { value, label: opt && opt.label })}
          />
        );
        break;
      case "message:multi":
        control = (
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            variant={variant}
            placeholder={data.placeholder || "Select..."}
            value={(answer && answer.answers ? answer.answers : []).map((a) => a.value)}
            options={(data.response || []).map((o) => ({ label: o.label, value: o.value }))}
            onChange={(values, opts) =>
              setAnswer(nodeId, {
                answers: (opts || []).map((o) => ({ value: o.value, label: o.label })),
              })
            }
          />
        );
        break;
      case "message:date":
        control = (
          <DatePicker
            style={{ width: "100%" }}
            variant={variant}
            placeholder={data.placeholder || "Select date..."}
            format={DATE_FORMAT}
            value={parsePickerValue(typeof answer === "string" ? answer : "", DATE_FORMAT)}
            onChange={(_, ds) => setAnswer(nodeId, ds || "")}
          />
        );
        break;
      case "message:time":
        control = (
          <TimePicker
            style={{ width: "100%" }}
            variant={variant}
            placeholder={data.placeholder || "Select time..."}
            format={TIME_FORMAT}
            value={parsePickerValue(typeof answer === "string" ? answer : "", TIME_FORMAT)}
            onChange={(_, ts) => setAnswer(nodeId, ts || "")}
          />
        );
        break;
      case "message:picker":
        control = (
          <InputNumber
            style={{ width: "100%" }}
            variant={variant}
            placeholder={data.placeholder || "Pick a value..."}
            min={data.min != null && data.min !== "" ? Number(data.min) : undefined}
            max={data.max != null && data.max !== "" ? Number(data.max) : undefined}
            step={data.step != null && data.step !== "" ? Number(data.step) : (data.decimal ? 0.1 : 1)}
            addonAfter={data.suffix || undefined}
            value={typeof answer === "string" && answer !== "" ? Number(answer) : undefined}
            onChange={(v) => setAnswer(nodeId, v == null ? "" : String(v))}
          />
        );
        break;
      case "message:slider": {
        const sliderOpts = Array.isArray(data.response) ? data.response : [];
        if (sliderOpts.length) {
          // labeled mode: discrete slider, one mark per option; store the label text
          // (scalar string) - same shape the numeric slider already stores.
          const marks = sliderOpts.reduce((acc, o, i) => {
            acc[i] = o.label;
            return acc;
          }, {});
          const selectedIndex = sliderOpts.findIndex((o) => o.label === answer);
          control = (
            <Slider
              style={{ width: "100%" }}
              min={0}
              max={sliderOpts.length - 1}
              step={1}
              marks={marks}
              value={selectedIndex >= 0 ? selectedIndex : undefined}
              onChange={(i) => setAnswer(nodeId, sliderOpts[i] ? sliderOpts[i].label : "")}
            />
          );
        } else {
          // numeric mode: unchanged
          // numeric picker variant: same control, but the step lives in `increment`
          control = (
            <InputNumber
              style={{ width: "100%" }}
              variant={variant}
              placeholder={data.placeholder || "Pick a value..."}
              min={data.min != null && data.min !== "" ? Number(data.min) : undefined}
              max={data.max != null && data.max !== "" ? Number(data.max) : undefined}
              step={data.increment != null && data.increment !== "" ? Number(data.increment) : 1}
              value={typeof answer === "string" && answer !== "" ? Number(answer) : undefined}
              onChange={(v) => setAnswer(nodeId, v == null ? "" : String(v))}
            />
          );
        }
        break;
      }
      case "message:input":
      default:
        control = (
          <Input
            variant={variant}
            placeholder={data.placeholder || ""}
            maxLength={1000}
            value={typeof answer === "string" ? answer : ""}
            onChange={(e) => setAnswer(nodeId, e.target.value)}
          />
        );
        break;
    }

    // Row spacing lives in CSS, so the label sits on the same line as its control
    // where the layout is horizontal (the invitation modal) and above it where it
    // is not (the narrow profile column).
    return (
      <div
        className={fieldClassName}
        key={nodeId}
        data-testid={`enrollment-field-${data.alias || nodeId}`}
        data-node-type={data.nodeType || "message:input"}
      >
        <p className="invite-user__field__name">{label}</p>
        {control}
      </div>
    );
  };

  return <div className="enrollment-invite-form">{nodeIds.map(renderField)}</div>;
};

export default EnrollmentForm;
